import { useMemo } from 'react';
import type { SignalData } from '../types/gateway';
import { useTranslation } from '../i18n/useTranslation';
import { PanelState } from './PanelState';
import { usePanelState } from '../hooks/usePanelState';
import { PANEL_SOURCE } from '../lib/panelSources';

interface TradeHistoryProps {
  signals: SignalData[];
  className?: string;
}

function formatTime(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatPrice(p: number): string {
  return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function TradeHistory({ signals, className = '' }: TradeHistoryProps) {
  const { t } = useTranslation();
  const tradesState = usePanelState('trades', PANEL_SOURCE.trades);
  const sorted = useMemo(
    () => [...signals].sort((a, b) => b.time - a.time),
    [signals]
  );

  return (
    <div className={`bg-card rounded border border-border flex flex-col ${className}`}>
      <div className="drag-handle h-6 flex items-center px-2 cursor-grab active:cursor-grabbing border-b border-border-subtle shrink-0">
        <span className="panel-header">{t('trades.header')}</span>
        <span className="ml-auto text-[8px] font-mono text-muted">{sorted.length}</span>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        <table className="w-full text-[9px] font-mono border-collapse" aria-label={t('trades.aria')}>
          <thead>
            <tr className="border-b border-border sticky top-0 bg-card">
              <th className="text-left px-1.5 py-1 text-muted-foreground font-normal">{t('trades.time')}</th>
              <th className="text-left px-1.5 py-1 text-muted-foreground font-normal">{t('trades.side')}</th>
              <th className="text-right px-1.5 py-1 text-muted-foreground font-normal">{t('trades.price')}</th>
              <th className="text-right px-1.5 py-1 text-muted-foreground font-normal">{t('trades.qty')}</th>
              <th className="text-right px-1.5 py-1 text-muted-foreground font-normal">{t('trades.pnl')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-1.5 py-3">
                  <PanelState state={tradesState === 'ready' ? 'live-empty' : tradesState} panelKey="trades" />
                </td>
              </tr>
            ) : (
              sorted.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-border-subtle/50 hover:bg-card-elevated transition-colors"
                >
                  <td className="px-1.5 py-1 text-muted-foreground">{formatTime(s.time)}</td>
                  <td className={`px-1.5 py-1 font-bold ${s.side === 'BUY' ? 'text-success' : 'text-danger'}`}>
                    {s.side}
                  </td>
                  <td className="px-1.5 py-1 text-right text-foreground">{formatPrice(s.price)}</td>
                  <td className="px-1.5 py-1 text-right text-muted-foreground">{s.qty}</td>
                  <td className={`px-1.5 py-1 text-right ${s.pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                    {s.pnl >= 0 ? '+' : ''}{s.pnl.toFixed(0)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
